import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonSpinner,
  IonText
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { signInModerator } from '../services/moderatorService';
import { useTranslation } from '../hooks/useTranslation';
import AnAppLogo from '../components/AnAppLogo';
import './ModLoginPage.css';

const ModLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const { t } = useTranslation();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError(null);
    try {
      await signInModerator(email, password);
      history.replace('/moderator');
    } catch (err: any) {
      const code = err.message || '';
      if (code === 'mod.error_not_mod') {
        setError(t('mod.error_not_mod'));
      } else if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
        setError(t('mod.error_invalid'));
      } else {
        setError(t('mod.error_invalid'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="mod-login-content" scrollY={false}>
        <div className="mod-login-container">
          <button className="mod-login-back-arrow" onClick={() => history.replace('/login')} aria-label="Volver">
            ←
          </button>
          <div className="mod-login-card">
            <div className="mod-login-brand">
              <AnAppLogo size={100} />
            </div>

            <h2 className="mod-login-title">{t('mod.login_title')}</h2>
            <p className="mod-login-subtitle">{t('mod.login_subtitle')}</p>

            <form onSubmit={handleLogin} className="mod-login-form">
              <div className="mod-login-field">
                <label className="mod-login-label">{t('mod.email_label')}</label>
                <input
                  type="email"
                  className="mod-login-input"
                  placeholder={t('mod.email_placeholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="mod-login-field">
                <label className="mod-login-label">{t('mod.password_label')}</label>
                <div className="mod-login-password-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="mod-login-input"
                    placeholder={t('mod.password_placeholder')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="mod-login-eye"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <IonText color="danger">
                  <p className="mod-login-error">{error}</p>
                </IonText>
              )}

              <button
                type="submit"
                className="mod-login-btn"
                disabled={loading || !email || !password}
              >
                {loading ? <IonSpinner name="crescent" /> : t('mod.login_button')}
              </button>
            </form>

          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ModLoginPage;
